import React, { useEffect, useRef, useState } from "react";

// Affiche le VRAI rapport HTML (srcdoc) dans une iframe redimensionnée à son contenu.
// Verrouillé : flou prononcé + défilement automatique lent. Déverrouillé : lisible et défilable.
export const ReportFrame = ({ html, locked }) => {
    const wrapRef = useRef(null);
    const frameRef = useRef(null);
    const [h, setH] = useState(1200);

    // Squelette flouté affiché immédiatement (avant que le vrai rapport soit prêt) : le fond défile dès l'arrivée.
    const placeholder = React.useMemo(() => {
        const secs = ["Ta première impression", "Structure du visage", "Peau et regard", "Cheveux et barbe",
            "Carrure et posture", "Style et image", "Tes axes prioritaires", "Ton plan sur 4 semaines"];
        const para = "Analyse détaillée de ton profil, avec des constats précis tirés de ta photo, de tes mesures et de tes réponses. Chaque point est expliqué puis relié à une action concrète.";
        const body = secs.map((s) => `<h2>${s}</h2><p>${para}</p><p>${para}</p>`).join("");
        return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
        <style>body{margin:0;padding:20px;background:#000;color:#fff;font-family:Archivo,Arial,sans-serif;line-height:1.6}
        h2{font-family:'Big Shoulders Display',Arial;font-weight:800;text-transform:uppercase;font-size:26px;margin:26px 0 10px;color:#fff}
        p{color:#cfcfcf;font-size:15px;margin:0 0 12px}</style></head><body>${body}</body></html>`;
    }, []);

    const src = html || (locked ? placeholder : "");

    const measure = () => {
        const doc = frameRef.current?.contentDocument;
        if (doc?.documentElement) setH(Math.max(600, doc.documentElement.scrollHeight));
    };

    const onLoad = () => {
        measure();
        const doc = frameRef.current?.contentDocument;
        if (doc?.fonts?.ready) doc.fonts.ready.then(measure);
        setTimeout(measure, 600);
        setTimeout(measure, 1800);
    };

    useEffect(() => {
        if (!locked) return undefined;
        const el = wrapRef.current;
        if (!el) return undefined;
        let raf; let dir = 1; let pos = el.scrollTop;
        const step = () => {
            const max = el.scrollHeight - el.clientHeight;
            if (max > 0) {
                pos += 0.45 * dir;
                if (pos >= max) { pos = max; dir = -1; }
                if (pos <= 0) { pos = 0; dir = 1; }
                el.scrollTop = Math.floor(pos);
            }
            raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [locked, h, src]);

    // Re-mesure régulière : garantit que le fond a toujours de la hauteur à faire défiler,
    // même si l'iframe se recharge (placeholder -> vrai rapport) ou si les polices décalent le contenu.
    useEffect(() => {
        if (!locked || !src) return undefined;
        const id = setInterval(measure, 1200);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locked, src]);

    return (
        <div
            ref={wrapRef}
            data-testid={locked ? "report-frame-locked" : "report-frame-unlocked"}
            data-clarity-mask="true"
            className="absolute inset-0 overflow-y-auto scrollbar-none"
            style={{
                WebkitOverflowScrolling: "touch",
                filter: locked ? "blur(9px)" : "blur(0px)",
                transform: locked ? "scale(1.02)" : "scale(1)",
                pointerEvents: locked ? "none" : "auto",
                transition: "filter 600ms var(--ease), transform 600ms var(--ease)",
            }}
        >
            {src ? (
                <iframe
                    ref={frameRef}
                    title="Analyse"
                    srcDoc={src}
                    onLoad={onLoad}
                    scrolling="no"
                    style={{ width: "100%", height: h, border: 0, display: "block", backgroundColor: "#000" }}
                />
            ) : null}
        </div>
    );
};
